/* Mezkla product page — vanilla JS.
   Handles: mount fade, hero parallax, detail-tile reveal, mobile thumbnail
   swap, color/size selection with cross-availability, accordion toggle,
   ajax add-to-cart with "Added ✓" feedback. No framework, no dependencies.
*/
(function () {
  'use strict';

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var root = document.querySelector('[data-product-section]');
  if (!root) return;

  /* ─── 1. Info-panel mount fade ─────────────────────────────────────── */
  var infoMount = root.querySelector('[data-info-mount]');
  if (infoMount) {
    window.setTimeout(function () {
      infoMount.setAttribute('data-mounted', 'true');
    }, 100);
  }

  /* ─── 2. Hero parallax ─────────────────────────────────────────────── */
  var hero = root.querySelector('[data-product-hero]');
  if (hero && !reduceMotion) {
    var raf = 0;
    function onScroll() {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(function () {
        var shift = Math.max(0, window.scrollY * 0.18);
        hero.style.transform = 'translateY(' + shift + 'px)';
      });
    }
    window.addEventListener('scroll', onScroll, { passive: true });
  }

  /* ─── 3. Detail-tile scroll reveal ─────────────────────────────────── */
  if ('IntersectionObserver' in window) {
    var io = new IntersectionObserver(function (entries) {
      for (var i = 0; i < entries.length; i++) {
        if (entries[i].isIntersecting) {
          entries[i].target.setAttribute('data-visible', 'true');
          io.unobserve(entries[i].target);
        }
      }
    }, { threshold: 0.15 });
    root.querySelectorAll('[data-reveal]').forEach(function (el) { io.observe(el); });
  } else {
    root.querySelectorAll('[data-reveal]').forEach(function (el) {
      el.setAttribute('data-visible', 'true');
    });
  }

  /* ─── 4. Back link — hijack to history.back() when we have history ── */
  var backLink = root.querySelector('[data-back-link]');
  if (backLink) {
    backLink.addEventListener('click', function (e) {
      var hasHistory = window.history.length > 1
        && document.referrer
        && document.referrer.indexOf(window.location.host) > -1;
      if (hasHistory) {
        e.preventDefault();
        window.history.back();
      }
      // No same-origin history → fall through to the href fallback.
    });
  }

  /* ─── 5. Variant selection ─────────────────────────────────────────── */
  var variantsEl = root.querySelector('[data-product-variants]');
  var configEl = root.querySelector('[data-option-config]');
  if (variantsEl && configEl) {
    var variants;
    var config;
    try {
      variants = JSON.parse(variantsEl.textContent);
      config = JSON.parse(configEl.textContent);
    } catch (e) {
      variants = null;
    }

    if (variants) {
      var colorIdx = config.color_index || 0;
      var sizeIdx = config.size_index || 0;
      var variantInput = root.querySelector('[data-variant-input]');
      var priceEl = root.querySelector('[data-price]');
      var colorLabel = root.querySelector('[data-color-label]');
      var atcBtn = root.querySelector('[data-atc]');
      var atcLabel = root.querySelector('[data-atc-label]');
      var colorButtons = root.querySelectorAll('[data-color-button]');
      var sizeButtons = root.querySelectorAll('[data-size-button]');

      var selectedColor = null;
      var selectedSize = null;

      // Seed selectedColor from the active swatch in the markup
      colorButtons.forEach(function (b) {
        if (b.getAttribute('data-active') === 'true') {
          selectedColor = b.getAttribute('data-option-value');
        }
      });

      function variantOptionValue(v, idx) {
        if (!idx) return null;
        return v['option' + idx];
      }

      function findVariant(color, size) {
        for (var i = 0; i < variants.length; i++) {
          var v = variants[i];
          var match = true;
          if (colorIdx > 0 && color != null) {
            if (variantOptionValue(v, colorIdx) !== color) match = false;
          }
          if (sizeIdx > 0 && size != null) {
            if (variantOptionValue(v, sizeIdx) !== size) match = false;
          }
          if (match) return v;
        }
        return null;
      }

      function formatPrice(cents) {
        // EU euro format: €XX,XX. Mirrors snippets/price-format.liquid.
        return '€' + (cents / 100).toFixed(2).replace('.', ',');
      }

      function update() {
        // Update color swatch active state + label
        colorButtons.forEach(function (b) {
          var v = b.getAttribute('data-option-value');
          var isActive = v === selectedColor;
          b.setAttribute('data-active', isActive ? 'true' : 'false');
          b.setAttribute('aria-pressed', isActive ? 'true' : 'false');
        });
        if (colorLabel && selectedColor) colorLabel.textContent = selectedColor;

        // Update size availability per-color + active state
        sizeButtons.forEach(function (b) {
          var size = b.getAttribute('data-option-value');
          var v = findVariant(selectedColor, size);
          var disabled = !v || !v.available;
          b.disabled = disabled;
          if (disabled) {
            b.setAttribute('aria-disabled', 'true');
          } else {
            b.removeAttribute('aria-disabled');
          }
          var isActive = size === selectedSize && !disabled;
          b.setAttribute('data-active', isActive ? 'true' : 'false');
          b.setAttribute('aria-pressed', isActive ? 'true' : 'false');
        });

        // If selected size is no longer available for the new color, clear it
        if (selectedSize) {
          var current = findVariant(selectedColor, selectedSize);
          if (!current || !current.available) selectedSize = null;
        }

        // Resolve current variant
        var variant = findVariant(selectedColor, selectedSize);
        if (variant) {
          if (variantInput) variantInput.value = variant.id;
          if (priceEl) priceEl.textContent = formatPrice(variant.price);
          if (atcBtn) {
            atcBtn.disabled = !variant.available;
            atcBtn.setAttribute('data-state', '');
          }
          if (atcLabel) {
            atcLabel.textContent = variant.available ? 'Add to cart' : 'Sold out';
          }
        } else {
          // No matching variant — usually means size hasn't been picked yet.
          if (atcBtn) {
            atcBtn.disabled = (sizeIdx > 0);
            atcBtn.setAttribute('data-state', '');
          }
          if (atcLabel) {
            atcLabel.textContent = sizeIdx > 0 ? 'Select a size' : 'Add to cart';
          }
        }
      }

      colorButtons.forEach(function (b) {
        b.addEventListener('click', function () {
          selectedColor = b.getAttribute('data-option-value');
          update();
        });
      });

      sizeButtons.forEach(function (b) {
        b.addEventListener('click', function () {
          if (b.disabled) return;
          selectedSize = b.getAttribute('data-option-value');
          update();
        });
      });

      update();

      /* ─── 6. Ajax add-to-cart with feedback ──────────────────────── */
      var form = root.querySelector('[data-product-form]');
      if (form && atcBtn) {
        form.addEventListener('submit', function (e) {
          e.preventDefault();
          if (atcBtn.disabled) return;

          atcBtn.disabled = true;
          if (atcLabel) atcLabel.textContent = 'Adding...';

          fetch('/cart/add.js', {
            method: 'POST',
            body: new FormData(form),
            headers: { 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' }
          })
            .then(function (res) {
              if (!res.ok) throw new Error('add failed');
              return res.json();
            })
            .then(function () {
              atcBtn.setAttribute('data-state', 'added');
              if (atcLabel) atcLabel.textContent = 'Added ✓';

              // Hand off to the cart drawer — it owns /cart.js fetching,
              // re-rendering, header count sync, and the slide-in animation.
              document.dispatchEvent(new CustomEvent('cart:open'));

              window.setTimeout(function () {
                atcBtn.setAttribute('data-state', '');
                atcBtn.disabled = false;
                if (atcLabel) atcLabel.textContent = 'Add to cart';
              }, 2000);
            })
            .catch(function () {
              atcBtn.disabled = false;
              if (atcLabel) atcLabel.textContent = 'Try again';
            });
        });

        /* ─── Express checkout — add to cart THEN redirect to /checkout.
           Distinguishes from Add to Cart (drawer) by skipping the drawer
           flow entirely and going straight to the Shopify checkout. */
        var buyNowBtn = root.querySelector('[data-buy-now]');
        if (buyNowBtn) {
          buyNowBtn.addEventListener('click', function () {
            if (buyNowBtn.disabled || atcBtn.disabled) return;
            buyNowBtn.disabled = true;
            var label = buyNowBtn.querySelector('span');
            var originalLabel = label ? label.textContent : '';
            if (label) label.textContent = 'Redirecting...';

            fetch('/cart/add.js', {
              method: 'POST',
              body: new FormData(form),
              headers: { 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' }
            })
              .then(function (res) {
                if (!res.ok) throw new Error('add failed');
                window.location.href = '/checkout';
              })
              .catch(function () {
                buyNowBtn.disabled = false;
                if (label) label.textContent = originalLabel || 'Express checkout';
              });
          });
        }
      }
    }
  }

  /* ─── 7. Accordion (single-open with measured-height transitions) ──
     Uses scrollHeight so close animations start from the real content
     height rather than a fixed max-height — the close feels smooth
     over its full 380ms instead of snapping in the last few pixels. */
  var accordionItems = root.querySelectorAll('[data-accordion-item]');

  function openItem(item) {
    var content = item.querySelector('.product-page__accordion-content');
    var toggle = item.querySelector('[data-accordion-toggle]');
    if (!content) return;
    item.setAttribute('data-open', 'true');
    if (toggle) toggle.setAttribute('aria-expanded', 'true');
    content.style.height = content.scrollHeight + 'px';
    // After the open transition, drop the inline height so dynamic content
    // (e.g. fonts loading later, viewport resize) doesn't get clamped.
    var onEnd = function (e) {
      if (e.propertyName !== 'height') return;
      if (item.getAttribute('data-open') === 'true') content.style.height = 'auto';
      content.removeEventListener('transitionend', onEnd);
    };
    content.addEventListener('transitionend', onEnd);
  }

  function closeItem(item) {
    var content = item.querySelector('.product-page__accordion-content');
    var toggle = item.querySelector('[data-accordion-toggle]');
    if (!content) return;
    // Set the current height first so the transition has a real start value
    // (height: auto can't transition).
    var current = content.scrollHeight;
    content.style.height = current + 'px';
    // Force reflow before flipping to 0 so the browser commits the start frame.
    void content.offsetHeight;
    item.setAttribute('data-open', 'false');
    if (toggle) toggle.setAttribute('aria-expanded', 'false');
    content.style.height = '0px';
  }

  // Initial state — open items get their content height set explicitly.
  accordionItems.forEach(function (item) {
    if (item.getAttribute('data-open') === 'true') {
      var content = item.querySelector('.product-page__accordion-content');
      if (content) content.style.height = content.scrollHeight + 'px';
      // After mount, drop to auto so it adapts to content changes.
      window.requestAnimationFrame(function () {
        if (content && item.getAttribute('data-open') === 'true') {
          content.style.height = 'auto';
        }
      });
    }
  });

  accordionItems.forEach(function (item) {
    var toggle = item.querySelector('[data-accordion-toggle]');
    if (!toggle) return;
    toggle.addEventListener('click', function () {
      var isOpen = item.getAttribute('data-open') === 'true';
      // Single-open: close every other open item, then toggle this one.
      accordionItems.forEach(function (other) {
        if (other !== item && other.getAttribute('data-open') === 'true') {
          closeItem(other);
        }
      });
      if (isOpen) closeItem(item);
      else openItem(item);
    });
  });

  /* ─── 8. "Size guide →" — opens the Size Guide accordion and brings it
     into view. Replaces the old link to /pages/size-guide (page removed). */
  var sgJump = root.querySelector('[data-size-guide-jump]');
  var sgItem = root.querySelector('[data-size-guide-item]');
  if (sgJump && sgItem) {
    sgJump.addEventListener('click', function () {
      accordionItems.forEach(function (other) {
        if (other !== sgItem && other.getAttribute('data-open') === 'true') {
          closeItem(other);
        }
      });
      if (sgItem.getAttribute('data-open') !== 'true') openItem(sgItem);
      sgItem.scrollIntoView({
        behavior: reduceMotion ? 'auto' : 'smooth',
        block: 'center'
      });
    });
  } else if (sgJump) {
    /* Guide markup missing (shouldn't happen — same Liquid condition
       renders both) — hide the dead button rather than no-op on tap. */
    sgJump.hidden = true;
  }

  /* ─── 9. Fullscreen image viewer (lightbox) ─────────────────────────
     Click any product image → open a full-bleed vertical scroll of every
     image, jumped to the one clicked. Close with ×, Esc, clicking the
     image, or clicking the letterbox area. */
  var lightbox = root.querySelector('[data-lightbox]');
  if (lightbox) {
    var lbScroll = lightbox.querySelector('[data-lightbox-scroll]');
    var lbClose = lightbox.querySelector('[data-lightbox-close]');
    var zoomables = root.querySelectorAll('[data-zoomable]');
    var lbLastFocus = null;
    var lbCloseTimer = 0;

    function lbScrollTo(index) {
      var target = lbScroll.querySelector('[data-lightbox-index="' + index + '"]');
      // __scroll is position:relative, items are its children, so offsetTop
      // is exactly the scroll offset that lands the image at the top.
      if (target) lbScroll.scrollTop = target.offsetTop;
    }

    function openLightbox(index) {
      lbLastFocus = document.activeElement;
      window.clearTimeout(lbCloseTimer);
      lightbox.hidden = false;
      document.body.style.overflow = 'hidden';
      lbScrollTo(index);
      // Next frame so the opacity transition runs from the now-displayed state.
      window.requestAnimationFrame(function () {
        lightbox.setAttribute('data-open', 'true');
      });
      if (lbClose) lbClose.focus();
    }

    function closeLightbox() {
      lightbox.setAttribute('data-open', 'false');
      document.body.style.overflow = '';
      if (reduceMotion) {
        lightbox.hidden = true;
      } else {
        lbCloseTimer = window.setTimeout(function () { lightbox.hidden = true; }, 240);
      }
      if (lbLastFocus && lbLastFocus.focus) lbLastFocus.focus();
    }

    zoomables.forEach(function (el) {
      el.addEventListener('click', function () {
        openLightbox(el.getAttribute('data-zoom-index') || '0');
      });
      el.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') {
          e.preventDefault();
          openLightbox(el.getAttribute('data-zoom-index') || '0');
        }
      });
    });

    if (lbClose) lbClose.addEventListener('click', closeLightbox);

    // Click on the image itself or the surrounding letterbox area closes.
    lbScroll.addEventListener('click', function () { closeLightbox(); });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && !lightbox.hidden) closeLightbox();
    });
  }
})();
